import React, { FunctionComponent, useCallback } from 'react';
import classNames from 'classnames';
import { Key, Scope, useShortcut } from '@trello/keybindings';
import { LayerManagerPortal, Layers } from 'app/src/components/LayerManager';
import styles from './Overlay.less';
import {
  ELEVATION_ATTR,
  useClickOutsideHandler,
  useCallbackRef,
  getHighestVisibleElevation,
} from '@trello/layer-manager';

export enum OverlayBackground {
  LIGHT = 'light',
  DARK = 'dark',
}

export enum OverlayAlignment {
  TOP = 'top',
}

interface OverlayProps {
  background: OverlayBackground;
  alignment?: OverlayAlignment;
  className?: string;
  closeOnEscape?: boolean;
  onClose: () => void;
}

const noop = () => {};

export const Overlay: FunctionComponent<OverlayProps> = ({
  background = OverlayBackground.LIGHT,
  alignment,
  className = '',
  closeOnEscape,
  onClose = noop,
  children,
}) => {
  const [contentsElement, contentsRef] = useCallbackRef<HTMLDivElement>();

  const handleEscape = useCallback(() => {
    if (closeOnEscape) {
      onClose();
    }
  }, [closeOnEscape, onClose]);

  useShortcut(handleEscape, {
    scope: Scope.Overlay,
    key: Key.Escape,
  });

  // Set up an 'elevation aware' outside click handler
  // to detect clicks outside the content of the overlay
  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (event.defaultPrevented) {
        return;
      }
      onClose();
    },
    [onClose],
  );

  // We really should be setting elevation back to 0, but sometimes a Popover is the parent of the <Overlay>
  // and so without using a higher elevation, opening the overlay would cause the Popover to close (which would
  // in turn unmount this Overlay)
  const elevation = getHighestVisibleElevation();
  useClickOutsideHandler({ element: contentsElement, handleClickOutside });

  return (
    <LayerManagerPortal layer={Layers.Overlay}>
      <div
        className={classNames(
          { [styles.alignTop]: alignment === OverlayAlignment.TOP },
          styles.overlay,
          styles[background],
          className,
        )}
      >
        <div
          ref={contentsRef}
          className={styles.contents}
          {...{ [ELEVATION_ATTR]: elevation + 1 }}
        >
          {children}
        </div>
      </div>
    </LayerManagerPortal>
  );
};
